import { AttachmentCategory, type AttachmentSelectDto, AttachmentType } from '@@types/api.types';
import { MAX_ATTACHMENT_SIZE_FIELD_DESCRIPTION } from '@azymmetric/shared/attachments/attachment.constants';
import { Modal } from '@components/core/modal.component';
import { ConfirmDeleteModal } from '@components/shared/confirm-delete-modal.component';
import { Button } from '@components/ui/button';
import { useAppForm } from '@components/ui/form/form';
import { FormDescription, FormField } from '@components/ui/form/shared.component';
import { cn } from '@lib/utils';
import type { AttachmentTargetType, BufferedAttachment } from '@queries/attachments.query';
import { type AttachmentFormData, buildAttachmentSchema } from '@schemas/attachment.schema';
import { createFormInvalidHandler, resetFieldMeta } from '@utils/form.util';
import { Loader2Icon } from 'lucide-react';
import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import {
  ATTACHMENT_TYPE_OPTIONS,
  getAttachmentTypeForKind,
  getDefaultCreateKind,
  getEditorKind,
  getInitialFeaturedValue,
  getSubmitButtonLabel,
  resolveAttachmentFileName,
} from './attachments-modal.util';
import { ImageAttachmentField } from './image-attachment-field.component';
import { type TechnologyAttachmentEditorKind, isTechnologyAttachmentImageKind } from './technology-attachments.util';
import { useAttachmentModalSubmit } from './use-attachment-modal-submit.hook';

type AttachmentsModalProps = {
  allowTechnologyImages?: boolean;
  attachmentToEdit: AttachmentSelectDto | null;
  bufferedAttachmentToEdit?: BufferedAttachment | null;
  category: AttachmentCategory;
  currentFeaturedTechnologyImageId?: string;
  defaultType?: AttachmentType;
  isOpen: boolean;
  onBufferRemove?: (clientId: string) => void;
  onBufferSave?: (data: BufferedAttachment) => void;
  onClose: () => void;
  targetId?: string;
  targetType?: AttachmentTargetType;
};

export const AttachmentsModal: FC<AttachmentsModalProps> = (props) => {
  const {
    allowTechnologyImages = false,
    attachmentToEdit,
    bufferedAttachmentToEdit,
    category,
    currentFeaturedTechnologyImageId,
    defaultType = AttachmentType.FILE,
    isOpen,
    onBufferRemove,
    onBufferSave,
    onClose,
    targetId,
    targetType = 'technology',
  } = props;

  const formResetRef = useRef<() => void>(() => {});

  const handleClose = useCallback((): void => {
    formResetRef.current();
    onClose();
  }, [onClose]);

  const [createKind, setCreateKind] = useState<TechnologyAttachmentEditorKind>(
    getDefaultCreateKind({ allowTechnologyImages, category, defaultType }),
  );
  const [featured, setFeatured] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const isBufferMode = Boolean(onBufferSave);
  const isEditingBuffered = Boolean(bufferedAttachmentToEdit);
  const isEditingExisting = Boolean(attachmentToEdit);
  const isEdit = isEditingExisting || isEditingBuffered;
  const editorKind = isEdit
    ? getEditorKind({
        allowTechnologyImages,
        attachmentToEdit,
        bufferedAttachmentToEdit,
        category,
        defaultType,
      })
    : createKind;
  const fileDisplayName = resolveAttachmentFileName({
    attachmentToEdit,
    bufferedAttachmentToEdit,
  });
  const formAttachmentType = getAttachmentTypeForKind(editorKind);
  const isImageKind = isTechnologyAttachmentImageKind(editorKind);
  const mode = isEdit ? 'edit' : 'create';

  const defaultValues: AttachmentFormData = {
    description: attachmentToEdit?.description ?? bufferedAttachmentToEdit?.description ?? '',
    file: bufferedAttachmentToEdit?.file ?? undefined,
    title: attachmentToEdit?.title ?? bufferedAttachmentToEdit?.title ?? '',
    type: formAttachmentType,
    url:
      (attachmentToEdit?.type === AttachmentType.URL ? attachmentToEdit?.url : undefined) ??
      (bufferedAttachmentToEdit?.type === AttachmentType.URL ? bufferedAttachmentToEdit?.url : undefined) ??
      '',
  };

  const { handleDelete, isPending, onSubmit } = useAttachmentModalSubmit({
    attachmentToEdit,
    bufferedAttachmentToEdit,
    category,
    currentFeaturedTechnologyImageId,
    editorKind,
    featured,
    formAttachmentType,
    isBufferMode,
    isEdit,
    isEditingExisting,
    onBufferRemove,
    onBufferSave,
    onClose: handleClose,
    targetId,
    targetType,
  });

  const form = useAppForm({
    canSubmitWhenInvalid: true,
    defaultValues,
    onSubmit: async ({ value }) => {
      await onSubmit(value);
    },
    onSubmitInvalid: createFormInvalidHandler(),
    validators: {
      onSubmit: buildAttachmentSchema(mode),
    },
  });

  formResetRef.current = () => form.reset();

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const nextEditorKind = getEditorKind({
      allowTechnologyImages,
      attachmentToEdit,
      bufferedAttachmentToEdit,
      category,
      defaultType,
    });

    form.reset();
    form.setFieldMeta('file', resetFieldMeta);
    form.setFieldMeta('url', resetFieldMeta);
    form.setFieldValue('description', attachmentToEdit?.description ?? bufferedAttachmentToEdit?.description ?? '');
    form.setFieldValue('file', bufferedAttachmentToEdit?.file ?? undefined);
    form.setFieldValue('title', attachmentToEdit?.title ?? bufferedAttachmentToEdit?.title ?? '');
    form.setFieldValue('type', getAttachmentTypeForKind(nextEditorKind));
    form.setFieldValue(
      'url',
      (attachmentToEdit?.type === AttachmentType.URL ? attachmentToEdit?.url : undefined) ??
        (bufferedAttachmentToEdit?.type === AttachmentType.URL ? bufferedAttachmentToEdit?.url : undefined) ??
        '',
    );
    setCreateKind(nextEditorKind);
    setFeatured(
      getInitialFeaturedValue({
        attachmentToEdit,
        bufferedAttachmentToEdit,
        editorKind: nextEditorKind,
        isEdit,
      }),
    );
    setIsDeleteModalOpen(false);
  }, [allowTechnologyImages, attachmentToEdit, bufferedAttachmentToEdit, category, defaultType, form, isEdit, isOpen]);

  const handleAttachmentTypeChange = (nextKind: TechnologyAttachmentEditorKind): void => {
    if (isEdit || nextKind === createKind) {
      return;
    }

    form.setFieldMeta('file', resetFieldMeta);
    form.setFieldMeta('url', resetFieldMeta);
    form.setFieldValue('file', undefined);
    form.setFieldValue('type', getAttachmentTypeForKind(nextKind));
    form.setFieldValue('url', '');
    setCreateKind(nextKind);
    setFeatured(false);
  };

  if (!isOpen) {
    return null;
  }

  const showDeleteButton = (isEditingBuffered && isBufferMode) || (isEditingExisting && !isBufferMode);
  const selectedImageUrl = attachmentToEdit?.inlineUrl ?? attachmentToEdit?.downloadUrl;

  return (
    <>
      <Modal
        centered
        footer={{
          leftButton: showDeleteButton ? (
            <Button
              disabled={isPending}
              variant="destructive"
              onClick={() => setIsDeleteModalOpen(true)}
            >
              Delete
            </Button>
          ) : undefined,
          rightButton: (
            <div className="flex justify-end">
              <Button
                disabled={isPending}
                onClick={() => form.handleSubmit()}
                type="button"
              >
                {isPending ? <Loader2Icon className="mr-1 size-3 animate-spin" /> : null}
                {getSubmitButtonLabel({ isEdit, kind: editorKind })}
              </Button>
            </div>
          ),
        }}
        header={{
          className: 'text-left',
          title: `${isEdit ? 'Update' : 'Add'} Attachment`,
        }}
        isOpen={isOpen}
        onClose={handleClose}
        width={isImageKind ? '680px' : '560px'}
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.AppForm>
            <div className="flex flex-col gap-4">
              {isEdit ? null : (
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-gray-700">Attachment Type</label>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    {ATTACHMENT_TYPE_OPTIONS.filter((option) => allowTechnologyImages || option.value !== 'IMAGE').map(
                      (option) => {
                        const Icon = option.icon;
                        const isSelected = createKind === option.value;

                        return (
                          <button
                            key={option.value}
                            className={cn(
                              'flex flex-1 items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors',
                              isSelected
                                ? 'border-[#3D2EFF] bg-[#3D2EFF]/10 text-[#3D2EFF]'
                                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50',
                            )}
                            onClick={() => handleAttachmentTypeChange(option.value)}
                            type="button"
                          >
                            <Icon size={16} />
                            {option.label}
                          </button>
                        );
                      },
                    )}
                  </div>
                </div>
              )}

              {isImageKind ? (
                <form.AppField name="file">
                  {() => (
                    <ImageAttachmentField
                      existingImageUrl={selectedImageUrl}
                      fileDisplayName={fileDisplayName}
                      isPending={isPending}
                      showReadOnlyField={false}
                    >
                      <form.AppField name="title">
                        {(field) => (
                          <field.TextField
                            label="Title"
                            placeholder="Image title"
                          />
                        )}
                      </form.AppField>
                    </ImageAttachmentField>
                  )}
                </form.AppField>
              ) : (
                <>
                  <form.AppField name="title">
                    {(field) => (
                      <field.TextField
                        label="Title"
                        placeholder="Attachment title"
                      />
                    )}
                  </form.AppField>

                  {formAttachmentType === AttachmentType.URL ? (
                    <form.AppField name="url">
                      {(field) => (
                        <field.TextField
                          label="URL"
                          placeholder="https://example.com"
                          type="url"
                        />
                      )}
                    </form.AppField>
                  ) : isEdit ? (
                    <FormField>
                      <label className="text-sm font-medium text-gray-700">File</label>
                      <div
                        aria-disabled={true}
                        className="h-10 w-full cursor-not-allowed truncate rounded-md border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-500 opacity-70"
                        title={fileDisplayName}
                      >
                        {fileDisplayName}
                      </div>
                      <FormDescription>To replace this file, delete the attachment and add a new one.</FormDescription>
                    </FormField>
                  ) : (
                    <form.AppField name="file">
                      {(field) => (
                        <field.FileField
                          accept="*/*"
                          description={MAX_ATTACHMENT_SIZE_FIELD_DESCRIPTION}
                          label="File"
                        />
                      )}
                    </form.AppField>
                  )}
                </>
              )}

              <form.AppField name="description">
                {(field) => (
                  <field.TextAreaField
                    label="Description"
                    placeholder={isImageKind ? 'Optional image description' : 'Optional description'}
                    rows={3}
                  />
                )}
              </form.AppField>

              {isImageKind ? (
                <div className="rounded-lg border border-gray-200 bg-white p-3">
                  <div className="flex items-start gap-3">
                    <input
                      checked={featured}
                      className="mt-1 size-4 accent-[#3D2EFF]"
                      type="checkbox"
                      onChange={(event) => setFeatured(event.target.checked)}
                    />
                    <div className="flex flex-col gap-1">
                      <label className="text-sm font-medium text-gray-700">Featured Image</label>
                      <p className="text-xs leading-5 text-gray-500">
                        Featured image appears in the technology header and attachments preview. Leave unchecked to keep
                        the current featured image, if any.
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </form.AppForm>
        </form>
      </Modal>

      {attachmentToEdit || bufferedAttachmentToEdit ? (
        <ConfirmDeleteModal
          isOpen={isDeleteModalOpen}
          itemName={isImageKind ? 'image' : 'attachment'}
          onConfirm={handleDelete}
          setIsOpen={setIsDeleteModalOpen}
        />
      ) : null}
    </>
  );
};
